import React, { useEffect, useState } from "react";
import {
    Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
    Button, Paper, Divider, Stack, Card, CardContent, alpha, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import BusinessIcon from "@mui/icons-material/Business";

import {
    collection,
    getDocs
} from "firebase/firestore";

import { db } from "../../firebase";

function Salaryslip() {
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [advances, setAdvances] = useState([]);

    const [selectedStaff, setSelectedStaff] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [salaryData, setSalaryData] = useState(null);

    const months = [
        "January", "February", "March", "April",
        "May", "June", "July", "August",
        "September", "October", "November", "December"
    ];
    const years = [2024, 2025, 2026, 2027, 2028, 2029, 2030];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [staffSnap, attendanceSnap, advanceSnap] = await Promise.all([
                getDocs(collection(db, "staff")),
                getDocs(collection(db, "staffattendance")),
                getDocs(collection(db, "staffadvances"))
            ]);

            setStaffList(staffSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setAttendance(attendanceSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setAdvances(advanceSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const generateSalarySlip = () => {
        if (!selectedStaff) {
            alert("Please Select Staff");
            return;
        }

        const staff = staffList.find(s => s.id === selectedStaff);
        if (!staff) {
            alert("Staff not found");
            return;
        }

        let present = 0;
        let absent = 0;
        let halfday = 0;

        // Calculate Attendance
        attendance.forEach((att) => {
            if (att.staffId !== selectedStaff) return;
            const attDate = new Date(att.date);
            const month = attDate.getMonth() + 1;
            const year = attDate.getFullYear();

            if (Number(month) === Number(selectedMonth) && Number(year) === Number(selectedYear)) {
                if (att.status === "Present") present++;
                else if (att.status === "Absent") absent++;
                else if (att.status === "Half Day") halfday++;
            }
        });

        // 3 Paid Holidays logic (Grace Absents)
        const paidHolidays = Math.min(absent, 3);
        const deductibleAbsents = Math.max(0, absent - 3);

        const daysInMonth = new Date(Number(selectedYear), Number(selectedMonth), 0).getDate();
        const monthlySalary = Number(staff.payment || 0);
        const dailyRate = monthlySalary / daysInMonth;

        const totalDaysToDeduct = deductibleAbsents + (halfday * 0.5);
        const totalPaidDays = daysInMonth - totalDaysToDeduct;

        const earnedSalary = Math.max(0, Math.round(monthlySalary - (totalDaysToDeduct * dailyRate)));
        const leaveDeductionAmount = Math.max(0, Math.round(monthlySalary - earnedSalary));

        // Fetch advance taken specifically in this selected month and year
        const monthAdvance = advances
            .filter(adv => {
                if (adv.staffId !== selectedStaff) return false;
                const d = new Date(adv.date);
                return d.getMonth() + 1 === Number(selectedMonth) && d.getFullYear() === Number(selectedYear);
            })
            .reduce((sum, adv) => sum + Number(adv.payment || 0), 0);

        const netSalary = Math.max(0, earnedSalary - monthAdvance);

        setSalaryData({
            staff,
            daysInMonth,
            present,
            absent,
            halfday,
            paidHolidays,
            deductibleAbsents,
            totalPaidDays,
            monthlySalary,
            dailyRate,
            earnedSalary,
            leaveDeductionAmount,
            monthAdvance,
            netSalary
        });
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f4f6f8", minHeight: "100vh" }}>
            
            {/* FILTER SECTION */}
            <Paper className="no-print" sx={{ p: 3, borderRadius: 4, mb: 4, boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                <Typography variant="h6" fontWeight="700" mb={3} display="flex" alignItems="center" gap={1}>
                    <ReceiptLongIcon color="primary" /> Generate Salary Slip
                </Typography>

                <Grid className="row" spacing={3} alignItems="center">
                    <Grid className="col-sm-3 mb-3" item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Staff Member</InputLabel>
                            <Select size="small" value={selectedStaff} label="Staff Member" onChange={(e) => setSelectedStaff(e.target.value)}>
                                {staffList.map((staff) => (
                                    <MenuItem key={staff.id} value={staff.id}>{staff.name} ({staff.post})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid className="col-sm-3 mb-3" item xs={6} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Month</InputLabel>
                            <Select size="small" value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>
                                {months.map((month, index) => <MenuItem key={index} value={index + 1}>{month}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid className="col-sm-3 mb-3" item xs={6} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Year</InputLabel>
                            <Select size="small" value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
                                {years.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid className="col-sm-3 mb-3" item xs={12} md={4}>
                        <Button variant="contained" size="large" fullWidth sx={{ height: 42, borderRadius: 2, fontWeight: 'bold', boxShadow: 0 }} onClick={generateSalarySlip}>
                            Generate Slip
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* SALARY SLIP DOCUMENT */}
            {salaryData && (
                <Paper
                    id="salary-slip"
                    className="printable-area"
                    sx={{
                        p: 4,
                        borderRadius: 2,
                        maxWidth: 1050,
                        margin: "auto",
                        boxShadow: "0px 10px 30px rgba(0,0,0,0.08)",
                        bgcolor: "#fff",
                        borderTop: "8px solid #1976d2"
                    }}
                >
                    {/* HEADER */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} pb={2} borderBottom="2px solid #eee">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box sx={{ p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                                <BusinessIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight="900" color="text.primary" letterSpacing={1}>TEJAS STONE CRUSHER</Typography>
                                <Typography variant="body2" color="text.secondary">Varuda Road, Dharashiv, Maharashtra</Typography>
                            </Box>
                        </Box>
                        <Box textAlign="right">
                            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ textTransform: 'uppercase' }}>Payslip</Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight="500">
                                {months[selectedMonth - 1]} {selectedYear}
                            </Typography>
                        </Box>
                    </Box>

                    {/* EMPLOYEE INFO */}
                    <Grid container spacing={4} mb={4}>
                        <Grid item xs={6}>
                            <Stack spacing={1}>
                                <Box display="flex"><Typography variant="body2" color="text.secondary" width={120}>Employee Name:</Typography> <Typography variant="body2" fontWeight="700">{salaryData.staff?.name}</Typography></Box>
                                <Box display="flex"><Typography variant="body2" color="text.secondary" width={120}>Designation:</Typography> <Typography variant="body2" fontWeight="700">{salaryData.staff?.post}</Typography></Box>
                                <Box display="flex"><Typography variant="body2" color="text.secondary" width={120}>Contact No:</Typography> <Typography variant="body2" fontWeight="700">{salaryData.staff?.mobile || "N/A"}</Typography></Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack spacing={1}>
                                <Box display="flex"><Typography variant="body2" color="text.secondary" width={140}>Total Days in Month:</Typography> <Typography variant="body2" fontWeight="700">{salaryData.daysInMonth || 0}</Typography></Box>
                                <Box display="flex"><Typography variant="body2" color="text.secondary" width={140}>Paid Days:</Typography> <Typography variant="body2" fontWeight="700" color="success.main">{salaryData.totalPaidDays || 0}</Typography></Box>
                                <Box display="flex"><Typography variant="body2" color="text.secondary" width={140}>Gross Salary:</Typography> <Typography variant="body2" fontWeight="700">₹{Number(salaryData.monthlySalary || 0).toFixed(2)}</Typography></Box>
                            </Stack>
                        </Grid>
                    </Grid>

                    {/* ATTENDANCE SUMMARY */}
                    <Box sx={{ bgcolor: "#f8f9fa", p: 2, borderRadius: 2, mb: 4, border: '1px solid #eee' }}>
                        <Typography variant="subtitle2" fontWeight="bold" mb={1.5} color="text.secondary" textTransform="uppercase">Attendance Breakdown</Typography>
                        <Grid container spacing={2} textAlign="center">
                            <Grid item xs={2.4}><Typography variant="caption" color="text.secondary">Present</Typography><Typography variant="h6" fontWeight="bold" color="#2e7d32">{salaryData.present || 0}</Typography></Grid>
                            <Grid item xs={2.4}><Typography variant="caption" color="text.secondary">Half Days</Typography><Typography variant="h6" fontWeight="bold" color="#ed6c02">{salaryData.halfday || 0}</Typography></Grid>
                            <Grid item xs={2.4}><Typography variant="caption" color="text.secondary">Total Absents</Typography><Typography variant="h6" fontWeight="bold" color="#d32f2f">{salaryData.absent || 0}</Typography></Grid>
                            <Grid item xs={2.4}><Typography variant="caption" color="text.secondary">Paid Holidays</Typography><Typography variant="h6" fontWeight="bold" color="primary.main">{salaryData.paidHolidays || 0}</Typography></Grid>
                            <Grid item xs={2.4}><Typography variant="caption" color="text.secondary">Unpaid Absents</Typography><Typography variant="h6" fontWeight="bold" color="error.main">{salaryData.deductibleAbsents || 0}</Typography></Grid>
                        </Grid>
                    </Box>

                    {/* EARNINGS & DEDUCTIONS TABLE */}
                    <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 4 }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: '50%', borderRight: '1px solid #e0e0e0' }}>Earnings</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>Deductions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    {/* Earnings Column */}
                                    <TableCell sx={{ verticalAlign: 'top', borderRight: '1px solid #e0e0e0', p: 0 }}>
                                        <Box display="flex" justifyContent="space-between" p={1.5}>
                                            <Typography variant="body2">Basic Salary</Typography>
                                            <Typography variant="body2" fontWeight="500">₹{Number(salaryData.monthlySalary || 0).toFixed(2)}</Typography>
                                        </Box>
                                    </TableCell>
                                    
                                    {/* Deductions Column */}
                                    <TableCell sx={{ verticalAlign: 'top', p: 0 }}>
                                        <Box display="flex" justifyContent="space-between" p={1.5} borderBottom="1px solid #f0f0f0">
                                            <Typography variant="body2">Leave Deduction ({(salaryData.deductibleAbsents || 0) + ((salaryData.halfday || 0) * 0.5)} days)</Typography>
                                            <Typography variant="body2" fontWeight="500" color="error.main">₹{Number(salaryData.leaveDeductionAmount || 0).toFixed(2)}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between" p={1.5}>
                                            <Typography variant="body2">Advance Taken (This Month)</Typography>
                                            <Typography variant="body2" fontWeight="500" color="error.main">₹{Number(salaryData.monthAdvance || 0).toFixed(2)}</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                
                                {/* Totals Row */}
                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0', p: 1.5 }}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="subtitle2" fontWeight="bold">Total Earnings</Typography>
                                            <Typography variant="subtitle2" fontWeight="bold">₹{Number(salaryData.monthlySalary || 0).toFixed(2)}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ p: 1.5 }}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="subtitle2" fontWeight="bold">Total Deductions</Typography>
                                            <Typography variant="subtitle2" fontWeight="bold" color="error.main">₹{Number((salaryData.leaveDeductionAmount || 0) + (salaryData.monthAdvance || 0)).toFixed(2)}</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* NET PAYABLE */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ bgcolor: '#e8f5e9', borderRadius: 2, mb: 6, border: '1px solid #c8e6c9' }}>
                        <Typography variant="h6" fontWeight="bold" color="#2e7d32">Net Salary Payable</Typography>
                        <Typography variant="h5" fontWeight="900" color="#2e7d32">₹{Number(salaryData.netSalary || 0).toFixed(2)}</Typography>
                    </Box>

                    {/* SIGNATURES */}
                    <Grid container spacing={4} mt={4}>
                        <Grid item xs={6} textAlign="center">
                            <Box sx={{ width: 150, mx: 'auto', borderBottom: '1px solid #999', mb: 1, height: 40 }}></Box>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Employer Signature</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="center">
                            <Box sx={{ width: 150, mx: 'auto', borderBottom: '1px solid #999', mb: 1, height: 40 }}></Box>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">Employee Signature</Typography>
                        </Grid>
                    </Grid>

                    {/* PRINT BUTTON */}
                    <Box textAlign="center" mt={6} className="no-print">
                        <Button variant="outlined" size="large" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ borderRadius: 2, px: 4, borderWidth: 2, fontWeight: 'bold' }}>
                            Print Payslip
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* PRINT CSS */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .printable-area, .printable-area * { visibility: visible; }
                    .printable-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        box-shadow: none !important;
                        border: none !important;
                        margin: 0 !important;
                        padding: 20px !important;
                    }
                    .no-print { display: none !important; }
                    @page { size: A4; margin: 10mm; }
                }
            `}</style>
        </Box>
    );
}

export default Salaryslip;